/* jshint esversion: 11 */

// Was ein Ziege-Raum ist und welche Einladungen und Events darin zählen.
//
// Ziege-Raum: Der Raum trägt das Marker-State-Event mit is_ziege: true, gesetzt vom
// Ersteller des Raums, und ist mit Megolm verschlüsselt. Nach den Standard-Power-Levels
// kann ein eingeladenes Mitglied keine State-Events setzen; der Marker stammt also
// vom Berater, der den Raum angelegt hat.
//
// Vor dem Beitritt sieht der Client nur den Invite State. Der Marker ist darin nur
// enthalten, wenn Synapse ihn per room_prejoin_state.additional_event_types
// mitschickt; fehlt er, wird nach dem Beitritt anhand des echten Raum-States geprüft.

import {EventType, KnownMembership} from "matrix-js-sdk";

export const ZIEGE_MARKER_STATE_EVENT_TYPE = "app.ziege.this_is_a_ziege_room";
export const ZIEGE_STATUS_UPDATE_MESSAGE_TYPE = "app.ziege.clientupdate";
export const MEGOLM_ENCRYPTION_ALGORITHM = "m.megolm.v1.aes-sha2";


function serverName(userId) {
    return userId.slice(userId.indexOf(":") + 1);
}

function stateEvent(room, type) {
    return room.currentState.getStateEvents(type, "");
}

function hasMegolmEncryption(room) {
    return stateEvent(room, EventType.RoomEncryption)?.getContent().algorithm === MEGOLM_ENCRYPTION_ALGORITHM;
}

function markerFromCreator(room) {
    const create = stateEvent(room, EventType.RoomCreate);
    const marker = stateEvent(room, ZIEGE_MARKER_STATE_EVENT_TYPE);
    return !!create && !!marker
        && marker.getContent().is_ziege === true
        && marker.getSender() === create.getSender();
}

export function isZiegeRoom(room) {
    return !!room && markerFromCreator(room) && hasMegolmEncryption(room);
}

function isMember(room, userId) {
    const membership = room.getMember(userId)?.membership;
    return membership === KnownMembership.Join || membership === KnownMembership.Invite;
}

// event.sender ist das Mitglied im Raum-State zum Zeitpunkt des Events.
function wasMemberWhenSent(room, event) {
    const membership = event.sender?.membership ?? room.getMember(event.getSender())?.membership;
    return membership === KnownMembership.Join || membership === KnownMembership.Invite;
}

export function inviterOf(room, ownUserId) {
    return room.getMember(ownUserId)?.events.member?.getSender() ?? null;
}

// Eine Einladung wird dem Nutzer nur vorgelegt, wenn sie von jemandem desselben
// Servers kommt und der Invite State nicht gegen einen Ziege-Raum spricht.
export function isAcceptableInvite(room, ownUserId) {
    const inviter = inviterOf(room, ownUserId);
    if (!inviter || serverName(inviter) !== serverName(ownUserId)) {
        return false;
    }
    if (!hasMegolmEncryption(room)) {
        return false;
    }
    const markerVisible = !!stateEvent(room, ZIEGE_MARKER_STATE_EVENT_TYPE);
    return !markerVisible || markerFromCreator(room);
}

// Ein Status-Update zählt nur, wenn es verschlüsselt ankam, sich entschlüsseln ließ,
// von einem Mitglied stammt und einen Klienten beschreibt, der Mitglied des Raums ist.
// Sonst könnte ein Raum-Mitglied Einträge unter fremder Matrix-ID anlegen.
export function isAcceptableStatusUpdate(room, event) {
    if (event.getType() !== ZIEGE_STATUS_UPDATE_MESSAGE_TYPE) {
        return false;
    }
    if (!event.isEncrypted() || event.isDecryptionFailure()) {
        console.warn("Unverschlüsseltes oder nicht entschlüsselbares Status-Update verworfen: ", event.getId());
        return false;
    }
    // Maßgeblich ist die Mitgliedschaft beim Senden: Updates einer Beratung, die den
    // Raum später verlassen hat, bleiben gültig.
    if (!wasMemberWhenSent(room, event)) {
        console.warn("Status-Update von Nicht-Mitglied verworfen: ", event.getSender());
        return false;
    }
    const clientId = event.getContent()?.id;
    if (!clientId || !isMember(room, clientId)) {
        console.warn("Status-Update für fremde Matrix-ID verworfen: ", clientId);
        return false;
    }
    return true;
}
